import {
	type DimensionConfig,
	Granularity,
	knownGranularity,
	type TimeDimensionConfig,
} from '../models/chart-config.model';

/**
 * Labels for the card's date when the card groups by it (JEP-10832). The query lambda
 * returns bucket values, never labels; tables (date column) and bar/column/donut
 * charts (date category) format them here. Line and area datetime axes do not.
 *
 * | granularity | value          | label                       |
 * |-------------|----------------|-----------------------------|
 * | DAY         | `YYYY-MM-DD`   | locale date (4 May 2026)    |
 * | WEEK        | `YYYY-MM-DD`   | Week of 4 May 2026          |
 * | MONTH       | `YYYY-MM`      | May 2026                    |
 * | QUARTER     | `YYYY-Qn` or `YYYY-MM` | Q2 2026             |
 * | YEAR        | `YYYY`         | 2026                        |
 * | DAY_OF_WEEK | `1` … `7`      | Monday … Sunday             |
 *
 * Every granularity but DAY_OF_WEEK also reads a full date: `YYYY-MM-DD`, an ISO
 * datetime (its date part) or epoch ms (its UTC date), so older shapes still label.
 * Values are calendar dates, read without a timezone shift. Callers keep the value
 * for sorting and use the label for display only.
 */

/** Calendar date; `month` is 1–12. */
interface CalendarDate {
	year: number;
	month: number;
	day: number;
}

/** `YYYY-MM-DD`, alone or as the date part of an ISO datetime. */
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;
const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;
const QUARTER_PATTERN = /^(\d{4})-Q([1-4])$/;
const YEAR_PATTERN = /^\d{4}$/;
const DAY_OF_WEEK_PATTERN = /^[1-7]$/;
const EPOCH_MS_PATTERN = /^\d{11,}$/;
/** Same threshold as the bar/column charts' timestamp check (~1970-04-26). */
const MIN_EPOCH_MS = 10_000_000_000;

function invalidValue(value: unknown, granularity: Granularity): Error {
	return new Error(
		`Invalid ${granularity} date value ${JSON.stringify(value)}`
	);
}

function calendarDate(
	year: number,
	month: number,
	day: number,
	value: unknown,
	granularity: Granularity
): CalendarDate {
	const utc = new Date(Date.UTC(year, month - 1, day));
	if (
		utc.getUTCFullYear() !== year ||
		utc.getUTCMonth() !== month - 1 ||
		utc.getUTCDate() !== day
	) {
		throw invalidValue(value, granularity);
	}
	return { year, month, day };
}

function valueText(value: unknown): string {
	return typeof value === 'number' && Number.isInteger(value)
		? String(value)
		: typeof value === 'string'
			? value.trim()
			: '';
}

/** `YYYY-MM-DD`, an ISO datetime's date part, or epoch ms as a UTC date. */
function parseDateValue(
	value: unknown,
	granularity: Granularity
): CalendarDate {
	const text = valueText(value);
	const match = DATE_PATTERN.exec(text);
	if (match) {
		const [year, month, day] = match.slice(1, 4).map(Number);
		return calendarDate(year, month, day, value, granularity);
	}
	if (EPOCH_MS_PATTERN.test(text) && Number(text) >= MIN_EPOCH_MS) {
		const utc = new Date(Number(text));
		return {
			year: utc.getUTCFullYear(),
			month: utc.getUTCMonth() + 1,
			day: utc.getUTCDate(),
		};
	}
	throw invalidValue(value, granularity);
}

/** `YYYY-MM`, or any full date {@link parseDateValue} reads. */
function parseMonthValue(
	value: unknown,
	granularity: Granularity
): CalendarDate {
	const match = MONTH_PATTERN.exec(valueText(value));
	if (!match) {
		return parseDateValue(value, granularity);
	}
	const [year, month] = match.slice(1).map(Number);
	return calendarDate(year, month, 1, value, granularity);
}

function parseQuarterValue(value: unknown): { year: number; quarter: number } {
	const quarterMatch = QUARTER_PATTERN.exec(valueText(value));
	if (quarterMatch) {
		return {
			year: Number(quarterMatch[1]),
			quarter: Number(quarterMatch[2]),
		};
	}
	const date = parseMonthValue(value, Granularity.Quarter);
	return { year: date.year, quarter: Math.floor((date.month - 1) / 3) + 1 };
}

/** `YYYY`, or any full date {@link parseDateValue} reads. */
function parseYearValue(value: unknown): number {
	const text = valueText(value);
	return YEAR_PATTERN.test(text)
		? Number(text)
		: parseDateValue(value, Granularity.Year).year;
}

/** ISO weekday 1 (Monday) … 7 (Sunday), as a number or a numeric string. */
function parseDayOfWeekValue(value: unknown): number {
	const text = valueText(value);
	if (!DAY_OF_WEEK_PATTERN.test(text)) {
		throw invalidValue(value, Granularity.DayOfWeek);
	}
	return Number(text);
}

function utcFormat(
	ms: number,
	locale: string | undefined,
	options: Intl.DateTimeFormatOptions
): string {
	return new Intl.DateTimeFormat(locale, {
		...options,
		timeZone: 'UTC',
	}).format(ms);
}

function formatDayLabel(date: CalendarDate, locale?: string): string {
	return utcFormat(Date.UTC(date.year, date.month - 1, date.day), locale, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
}

/**
 * Label for one bucket value of the card's date (see the table above). A value
 * without the shape its granularity returns shows as is: the previous granularity's
 * data is still on screen while a granularity change refetches.
 * `locale` goes to `Intl` (omit it for the viewer's locale).
 */
export function formatDateBucketLabel(
	value: unknown,
	granularity: Granularity,
	locale?: string
): string {
	try {
		return bucketLabel(value, granularity, locale);
	} catch {
		return value == null ? '' : String(value);
	}
}

function bucketLabel(
	value: unknown,
	granularity: Granularity,
	locale: string | undefined
): string {
	switch (granularity) {
		case Granularity.Day:
			return formatDayLabel(parseDateValue(value, granularity), locale);
		case Granularity.Week:
			return `Week of ${formatDayLabel(
				parseDateValue(value, granularity),
				locale
			)}`;
		case Granularity.Month: {
			const date = parseMonthValue(value, granularity);
			return utcFormat(Date.UTC(date.year, date.month - 1, 1), locale, {
				month: 'long',
				year: 'numeric',
			});
		}
		case Granularity.Quarter: {
			const { year, quarter } = parseQuarterValue(value);
			return `Q${quarter} ${year}`;
		}
		case Granularity.Year:
			return String(parseYearValue(value));
		case Granularity.DayOfWeek:
			// 1 Jan 2024 is a Monday, so day n of that week is ISO weekday n.
			return utcFormat(
				Date.UTC(2024, 0, parseDayOfWeekValue(value)),
				locale,
				{ weekday: 'long' }
			);
	}
	throw new Error(`Invalid granularity ${JSON.stringify(granularity)}`);
}

/** The card's date when the card groups by it. */
export interface GroupedCardDate {
	/** `timeDimension.dimension`, also one of `data.dimensions`. */
	dimension: string;
	granularity: Granularity;
}

type CardDateData =
	| {
			dimensions?: ReadonlyArray<Pick<DimensionConfig, 'name'>>;
			timeDimension?: Pick<
				TimeDimensionConfig,
				'dimension' | 'granularity'
			>;
	  }
	| undefined;

/**
 * The card's date and its granularity when the card groups by it: the
 * `timeDimension.dimension` is one of `data.dimensions` and a granularity is set
 * (the query lambda's rule). `undefined` when the date only filters, or when the
 * stored granularity is unknown (see {@link knownGranularity}): plain labels then.
 */
export function groupedCardDate(
	data: CardDateData
): GroupedCardDate | undefined {
	const dimension = data?.timeDimension?.dimension?.trim() ?? '';
	if (
		!dimension ||
		!data?.dimensions?.some(d => d?.name?.trim() === dimension)
	) {
		return undefined;
	}
	const granularity = knownGranularity(data?.timeDimension?.granularity);
	return granularity ? { dimension, granularity } : undefined;
}

/**
 * Bar/column/donut: the granularity of the category (`dimensions[0]`) when it is the
 * card's grouped date, else `undefined` (an ordinary category).
 */
export function chartDateCategoryGranularity(
	data: CardDateData
): Granularity | undefined {
	const grouped = groupedCardDate(data);
	const category = data?.dimensions?.[0]?.name?.trim();
	return grouped && grouped.dimension === category
		? grouped.granularity
		: undefined;
}
